/**
 * Schedule Context - Main Export
 * 
 * This file composes ScheduleStateContext and ScheduleOperationsContext
 * to provide a unified API for the schedule system.
 * 
 * This replaces the old monolithic ScheduleContext.js file.
 * 
 * @module contexts/schedule
 */

import React from 'react';
import { 
    ScheduleStateProvider, 
    useScheduleState,
    useScheduleDispatch,
    useScheduleStateValue,
    useStaffSchedule,
    useScheduleLoading,
    useScheduleError,
    useTimeSlotSelection,
    useScheduleView,
    actionTypes,
    initialState
} from './ScheduleStateContext';
import { 
    ScheduleOperationsProvider, 
    useScheduleOperations 
} from './ScheduleOperationsContext';

// Re-export utility functions for easy access
export {
    // Time slot utilities
    TIME_SLOT_MINUTES,
    START_HOUR,
    END_HOUR,
    TOTAL_SLOTS,
    TIME_SLOT_BOUNDARIES,
    SHIFT_CONSTRAINTS,
    timeToSlot,
    slotToTime,
    parseTimeString,
    minutesToTimeString,
    formatTimeSlot,
    formatDuration,
    formatTimeRange,
    generateSlotId,
    parseSlotId,
    isValidSlotId,
    generateTimeSlots,
    generateTimeSlotsForHour,
    calculateSelectedSlots,
    calculateHoursFromSelection,
    calculateSlotDuration,
    isSlotWithinBusinessHours,
    getNearestSlot,
    getCurrentSlot,
    isCurrentlyBusinessHours,
    compareSlots,
    areSlotsAdjacent,
    getTimeSlotConfig,
    TimeSlotUtils
} from '../../utils/TimeSlotUtils';  // ← FIXED: Removed /schedule

export {
    // Validation utilities
    VALIDATION_ERRORS,
    VALIDATION_WARNINGS,
    validateDateFormat,
    isDateInPast,
    validateTimeFormat,
    validateBusinessHours,
    validateShiftDuration,
    calculateShiftDuration,
    validateShift,
    validateShiftUpdate,
    doTimeRangesOverlap,
    checkShiftOverlap,
    formatOverlapError,
    checkBatchOverlaps,
    validateWeeklyHours,
    validateRestPeriod,
    validateShiftOrder,
    validateShiftBatch,
    validateTemplate,
    ScheduleValidationUtils
} from '../../utils/ScheduleValidationUtils';  // ← FIXED: Removed /schedule

// Re-export action types and initial state for advanced usage
export { actionTypes, initialState };

// Re-export individual hooks for optimization
export {
    useScheduleState,
    useScheduleDispatch,
    useScheduleStateValue,
    useStaffSchedule,
    useScheduleLoading,
    useScheduleError,
    useTimeSlotSelection,
    useScheduleView,
    useScheduleOperations
};

// ==================== COMPOSED PROVIDER ====================

/**
 * Combined Schedule Provider
 * Wraps both ScheduleStateProvider and ScheduleOperationsProvider
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element}
 * 
 * @example
 * <ScheduleProvider>
 *   <SchedulePlannerPage />
 * </ScheduleProvider>
 */
export const ScheduleProvider = ({ children }) => {
    return (
        <ScheduleStateProvider>
            <ScheduleOperationsProvider>
                {children}
            </ScheduleOperationsProvider>
        </ScheduleStateProvider>
    );
};

// ==================== UNIFIED HOOK ====================

/**
 * Main hook for accessing schedule system
 * Combines state and operations into a single API
 * 
 * This hook provides the same API as the old ScheduleContext for backward compatibility
 * 
 * @returns {Object} Complete schedule context with state and operations
 * 
 * @example
 * function MyComponent() {
 *   const {
 *     // State
 *     schedules,
 *     holidays,
 *     selectedStaff,
 *     currentDate,
 *     viewMode,
 *     loading,
 *     errors,
 *     
 *     // Operations
 *     createShift,
 *     updateShift,
 *     deleteShift,
 *     setSelectedStaff,
 *     setViewMode
 *   } = useSchedule();
 *   
 *   return <div>...</div>;
 * }
 */
export const useSchedule = () => {
    const { state } = useScheduleState();
    const operations = useScheduleOperations();
    
    // Combine state and operations into a single object
    return {
        // ==================== State (from ScheduleStateContext) ====================
        
        // Data state
        schedules: state.schedules,
        holidays: state.holidays,
        templates: state.templates,
        selectedStaff: state.selectedStaff,
        selectedStaffList: state.selectedStaffList,
        
        // Time slot selection state
        timeSlotSelection: state.timeSlotSelection,
        
        // View state
        currentDate: state.currentDate,
        viewMode: state.viewMode,
        dateRange: state.dateRange,
        
        // Schedule calculations
        schedulePeriods: state.schedulePeriods,
        weeklyHours: state.weeklyHours,
        monthlyHours: state.monthlyHours,
        adjustedWeeklyHours: state.adjustedWeeklyHours,
        adjustedMonthlyHours: state.adjustedMonthlyHours,
        realTimeCalculations: state.realTimeCalculations,
        
        // UI state
        loading: state.loading,
        errors: state.errors,
        
        // Subscriptions
        subscriptions: state.subscriptions,
        
        // Permissions
        canManageAllStaff: state.canManageAllStaff,
        manageableStaffIds: state.manageableStaffIds,
        userDepartmentIds: state.userDepartmentIds,
        
        // Optimistic updates
        optimisticUpdates: state.optimisticUpdates,
        
        // ==================== Operations (from ScheduleOperationsContext) ====================
        
        // Data loading
        loadScheduleForStaff: operations.loadScheduleForStaff,
        loadSchedulesForStaffList: operations.loadSchedulesForStaffList,
        loadHolidays: operations.loadHolidays,
        loadTemplates: operations.loadTemplates,
        
        // Time slot selection management
        startTimeSlotSelection: operations.startTimeSlotSelection,
        updateTimeSlotSelection: operations.updateTimeSlotSelection,
        endTimeSlotSelection: operations.endTimeSlotSelection,
        clearTimeSlotSelection: operations.clearTimeSlotSelection,
        setDragState: operations.setDragState,
        updateHoverSlot: operations.updateHoverSlot,
        calculateSelectedSlots: operations.calculateSelectedSlots,
        createShiftFromSelection: operations.createShiftFromSelection,
        
        // CRUD operations
        createShift: operations.createShift,
        updateShift: operations.updateShift,
        deleteShift: operations.deleteShift,
        
        // Bulk operations
        createShiftsBatch: operations.createShiftsBatch,
        applyTemplate: operations.applyTemplate,
        copyWeekSchedule: operations.copyWeekSchedule,
        saveWeeklySchedule: operations.saveWeeklySchedule, 
        saveMultipleWeeklySchedules: operations.saveMultipleWeeklySchedules, 

        // Selection management
        setSelectedStaff: operations.setSelectedStaff,
        setSelectedStaffList: operations.setSelectedStaffList,
        addToStaffSelection: operations.addToStaffSelection,
        removeFromStaffSelection: operations.removeFromStaffSelection,
        
        // View management
        setCurrentDate: operations.setCurrentDate,
        setViewMode: operations.setViewMode,
        navigateDate: operations.navigateDate,
        
        // Utility functions
        clearError: operations.clearError,
        getScheduleForStaff: operations.getScheduleForStaff,
        getSchedulePeriodForStaff: operations.getSchedulePeriodForStaff,
        getHoursForStaff: operations.getHoursForStaff,
        canManageStaff: operations.canManageStaff,
        validateShiftData: operations.validateShiftData,
        checkForShiftOverlap: operations.checkForShiftOverlap
    };
};

// ==================== DEFAULT EXPORT ====================

/**
 * Default export for the schedule context module
 * Provides both the provider and the hook
 */
export default {
    Provider: ScheduleProvider,
    useSchedule,
    useScheduleState,
    useScheduleOperations,
    actionTypes,
    initialState
};